import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Col, Row, Container } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Fields, isDirty } from 'redux-form';
import gql from 'graphql-tag';
import formatInitialValues from '../../lib/formatInitialValues';
import BlogCategoryDropdown from '../../containers/BlogCategoryDropdown';
import BlogMediumField from '../../containers/BlogMediumField';
import BlogTitle from '../../containers/BlogTitle';
import { AsyncFeedback } from '../../../src/web/components';
import Button from '../../../modules/react/components/stateless/Button';
import Form from '../../../modules/react/components/stateful/Form';
import Input from '../../../modules/react/components/stateful/Input';
import Field from '../../../modules/react/components/stateful/Field';
import DateInput from '../../../src/web/components/form/inputs/DateInput/DateInput';
import DraftInput from '../../../src/web/components/form/inputs/Draft/Draft';
import styles from './styles.scss';

const propTypes = {
  dirty: PropTypes.bool.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string),
  isSubmitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  post: PropTypes.shape({
    author: PropTypes.string,
    category: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    content: PropTypes.object,
    date: PropTypes.string,
    snippet: PropTypes.string,
    title: PropTypes.string,
  }),
  successMessage: PropTypes.string,
};

const defaultProps = {
  errors: undefined,
  post: undefined,
  successMessage: 'Success!',
};

const FORM_NAME = 'blog-post';

const defaultValues = {
  author: undefined,
  content: {
    blocks: [
      {
        key: 'bkj2s',
        text: 'Lorem ipsum dolor sit amet.',
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
    ],
    entityMap: [{ 0: { type: 'LINK', mutability: 'MUTABLE', data: { href: 'https://wwww.google.com' } } }],
  },
  date: new Date().toISOString(),
  snippet: '',
  title: undefined,
  featuredImage: {
    id: undefined,
    altText: undefined,
    title: undefined,
  },
};

const initialize = post => formatInitialValues(
  defaultValues, { ...post, featuredImage: { id: post?.mediumId } },
);

// TODO: Swap out <Form /> (which uses redux-form)
const BlogPostForm = ({
  dirty,
  errors,
  isSubmitting,
  onSubmit,
  post,
  successMessage,
}) => {
  // The react-select Creatable component used in BlogCategoryDropdown doesn't maintain
  // the value on the input field, so we have to intercept it on change.
  const [categoryName, setCategoryName] = useState(post?.category?.name);
  const handleCategoryChange = category => setCategoryName(category);

  const handleSubmit = data => onSubmit({ ...data, categoryName });

  const hasChanges = dirty || categoryName !== post?.category?.name;

  return (
    <>
      <Form
        enableReinitialize
        form={FORM_NAME}
        initialValues={initialize(post)}
        onSubmit={handleSubmit}
      >
        <Container fluid className={styles.innerGrid}>
          <Row className={styles.titleRow}>
            <Col xs={9}>
              <BlogTitle />
            </Col>
            <Col xs={3} className={styles.buttonColumn}>
              <Container fluid className={styles.buttonContainer}>
                <Row>
                  <Col xs={6}>
                    <Button
                      block
                      variant="default"
                      as={Link}
                      to="/admin/blog"
                    >
                      {hasChanges ? 'Cancel & Close' : 'Close'}
                    </Button>
                  </Col>
                  <Col xs={6}>
                    <Button
                      block
                      variant="success"
                      disabled={isSubmitting || !hasChanges}
                      type="submit"
                    >
                      Publish
                    </Button>
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
        </Container>
        <Container fluid>
          <Row>
            <Col xs={9}>
              <Container fluid className={styles.innerGrid}>
                <Row>
                  <Col xs={12}>
                    <Field
                      label="Title"
                      name="title"
                      component={Input}
                      required
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Fields
                      names={['content', 'snippet']}
                      component={DraftInput}
                      label="Post"
                      required
                    />
                  </Col>
                </Row>
              </Container>
            </Col>
            <Col xs={3}>
              <Container fluid className={styles.innerGrid}>
                <Row>
                  <Col xs={12}>
                    <Field
                      label="Date"
                      name="date"
                      component={DateInput}
                      required
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Field
                      label="Author"
                      name="author"
                      component={Input}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Field
                      label="Category"
                      name="category.name"
                      component={BlogCategoryDropdown}
                      currentCategory={post?.category?.name}
                      onCategoryChange={handleCategoryChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Fields
                      label="Featured Image"
                      names={['featuredImage.id', 'featuredImage.altText', 'featuredImage.title']}
                      component={BlogMediumField}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <AsyncFeedback
                      error={errors}
                      isSaving={isSubmitting}
                      success={successMessage}
                    />
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
        </Container>
      </Form>
    </>
  );
};

BlogPostForm.propTypes = propTypes;
BlogPostForm.defaultProps = defaultProps;

BlogPostForm.fragments = {
  post: gql`
    fragment BlogPostFormBlogPost on BlogPost {
      title
      author
      content
      snippet
      category {
        id
        name
      }
      date
      mediumId
    }
  `,
};

export default connect(
  state => ({
    dirty: isDirty(FORM_NAME)(state),
  }),
  {},
)(BlogPostForm);
